// Shared ESPN-style scrollable stat columns + pinned SAB PTS column.
// Render this alongside your own sticky left column in a flex container.

import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatColumn, SeasonStats, getStatDisplay, getSabPtsDisplay } from './statsTableDefs';

export interface StatsRow {
  id: string;
  stats: SeasonStats | null;
  sabPts: number;
  /** Override games_played for per-game calculations (e.g. time-filtered window) */
  filteredGamesPlayed?: number;
}

interface StatsColumnsProps {
  rows: StatsRow[];
  columns: StatColumn[];
  statView: 'season' | 'pergame';
  sortKey?: string | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  rowHeight?: string;
  headerHeight?: string;
  /** Extra empty rows appended after data rows (for empty roster slots) */
  emptyRowCount?: number;
  /** Index of the first row in the underlying full list (for stripe parity when virtualizing) */
  startIndex?: number;
  /** Vertical spacer above the visible rows (for virtualization) */
  paddingTop?: number;
  /** Vertical spacer below the visible rows (for virtualization) */
  paddingBottom?: number;
}

const SortArrow = ({ colKey, sortKey, sortDir }: { colKey: string; sortKey?: string | null; sortDir?: 'asc' | 'desc' }) => {
  if (sortKey !== colKey) return null;
  return sortDir === 'desc'
    ? <ChevronDown className="w-3 h-3 inline ml-0.5" />
    : <ChevronUp className="w-3 h-3 inline ml-0.5" />;
};

const StatsColumns = ({
  rows,
  columns,
  statView,
  sortKey,
  sortDir,
  onSort,
  rowHeight = 'h-11',
  headerHeight = 'h-9',
  emptyRowCount = 0,
  startIndex = 0,
  paddingTop = 0,
  paddingBottom = 0,
}: StatsColumnsProps) => {
  return (
    <>
      {/* Scrollable stat columns */}
      <div className="flex-1 overflow-x-auto">
        {/* Header */}
        <div className={cn("flex items-center bg-muted/50 text-muted-foreground text-[10px] font-semibold uppercase tracking-wider border-b border-border", headerHeight)}>
          {columns.map(col => (
            <button
              key={col.key}
              onClick={() => onSort?.(col.key)}
              className={cn(
                "w-12 text-center px-0.5 hover:text-foreground transition-colors whitespace-nowrap flex-shrink-0",
                sortKey === col.key && "text-foreground",
                !onSort && "cursor-default"
              )}
              disabled={!onSort}
            >
              {col.label}<SortArrow colKey={col.key} sortKey={sortKey} sortDir={sortDir} />
            </button>
          ))}
        </div>
        {paddingTop > 0 && <div style={{ height: paddingTop }} />}
        {/* Data rows */}
        {rows.map((row, idx) => (
          <div
            key={row.id}
            className={cn(
              "flex items-center border-b border-border/40",
              rowHeight,
              (idx + startIndex) % 2 === 1 && "bg-muted/10"
            )}
          >
            {columns.map(col => (
              <div key={col.key} className="w-12 text-center text-xs text-muted-foreground px-0.5 flex-shrink-0 tabular-nums">
                {getStatDisplay(col, row.stats, statView)}
              </div>
            ))}
          </div>
        ))}
        {paddingBottom > 0 && <div style={{ height: paddingBottom }} />}
        {/* Empty rows for unfilled slots */}
        {Array.from({ length: emptyRowCount }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className={cn(
              "flex items-center border-b border-border/40",
              rowHeight,
              (rows.length + i) % 2 === 1 && "bg-muted/10"
            )}
          >
            {columns.map(col => (
              <div key={col.key} className="w-12 text-center text-xs text-muted-foreground/40 px-0.5 flex-shrink-0">
                -
              </div>
            ))}
          </div>
        ))}
        {rows.length === 0 && emptyRowCount === 0 && paddingTop === 0 && paddingBottom === 0 && <div className="h-24" />}
      </div>

      {/* Pinned right: SAB PTS — hidden on mobile because the inline SAB column
          in the sticky-left section (added by SAB-263 MLB-061 for Players and
          SAB-255 MLB-023 for MyTeam) already covers mobile's SAB display. Keeping
          both on mobile makes the layout overflow the viewport and clips the right
          edge of this column ("SAB PT" partial header, "322" instead of "322.5"). */}
      <div className="hidden sm:block flex-shrink-0 z-10 bg-card border-l border-border">
        <div className={cn("flex items-center justify-center bg-muted/50 border-b border-border", headerHeight)}>
          <button
            onClick={() => onSort?.('SPTS')}
            className={cn(
              "text-[10px] font-bold uppercase tracking-wider px-2 whitespace-nowrap text-orange-400 hover:text-orange-300 transition-colors",
              sortKey === 'SPTS' && "text-orange-300",
              !onSort && "cursor-default"
            )}
            disabled={!onSort}
          >
            SAB PTS<SortArrow colKey="SPTS" sortKey={sortKey} sortDir={sortDir} />
          </button>
        </div>
        {paddingTop > 0 && <div style={{ height: paddingTop }} />}
        {rows.map((row, idx) => (
          <div
            key={row.id}
            className={cn(
              "flex items-center justify-center border-b border-border/40 px-2",
              rowHeight,
              (idx + startIndex) % 2 === 1 && "bg-muted/10"
            )}
          >
            <span className={cn(
              "text-xs font-bold tabular-nums",
              (row.stats?.games_played || 0) > 0 && row.sabPts !== 0 ? (row.sabPts > 0 ? "text-orange-400" : "text-green-400") : "text-muted-foreground"
            )}>
              {getSabPtsDisplay(row.sabPts, statView, row.filteredGamesPlayed ?? row.stats?.games_played ?? 0)}
            </span>
          </div>
        ))}
        {paddingBottom > 0 && <div style={{ height: paddingBottom }} />}
        {/* Empty rows for unfilled slots */}
        {Array.from({ length: emptyRowCount }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className={cn(
              "flex items-center justify-center border-b border-border/40 px-2",
              rowHeight,
              (rows.length + i) % 2 === 1 && "bg-muted/10"
            )}
          >
            <span className="text-xs text-muted-foreground/40">-</span>
          </div>
        ))}
        {rows.length === 0 && emptyRowCount === 0 && paddingTop === 0 && paddingBottom === 0 && <div className="h-24" />}
      </div>
    </>
  );
};

export default StatsColumns;
